// @flow
import * as actionTypes from '../constants/action_types'
import type { FicusDataType } from '../components/frameworks'
import { round } from '../util/numbers'

export type StatsType = {
    min?: number,
    '1q'?: number,
    mean?: number,
    median?: number,
    '3q'?: number,
    max?: number,
    range?: number,
    sd?: number,
}

// A tuple of [Raster value, Raster name]
export type FicusValueType = [number, string]

// Locations on a map and any associated statistics or histograms of realizations
export type LocationType = {
    // native project map point (3857)
    coordinate: [number, number],
    // histogram of realization values
    hist: number[],
    lonlat: [number, number],
    // Placemark ID in map
    pmId: string,
    stats: StatsType,
    // Realization values
    values: Array<FicusValueType>,
    // Gray swan values for the location
    valuesGrayswan: Array<number>,
    // An identifier for the location, typically the lon-lat
    title: string,
}

export type AnalysisPanelType = {
    // location whose values should be displayed in the analysis panel.
    analysisLocation?: LocationType,
    grayswan: Array<[number, string]>,
    iterationType: 'default' | 'value' | 'grayswan',
    mapLocations: LocationType[],
    // For realization plot
    realization?: number,
    visible: boolean,
}

export type AnalysisPanelActionType = {
    type: string,
    ficusData: FicusDataType,
    location: LocationType,
    newState: {},
}

const initialState = {
    grayswan: [],
    iterationType: 'default',
    mapLocations: [],
    visible: true,
}

const getGrayswansForLocation = (location: LocationType) => {
    const median = location.stats ? location.stats.median : undefined
    if (median && location.values.length) {
        return location.values.map(v => [(median - v[0]) ** 2, ''])
    }
    return []
}
const getLocationTitle = (location: LocationType) => `${round(location.lonlat[1], 3).toFixed(3)}, ${round(location.lonlat[0], 3).toFixed(3)}`

export default function analysisPanelData(state: AnalysisPanelType = initialState, action: AnalysisPanelActionType) {
    let newState = { ...state }
    if (action.type === actionTypes.REMOVE_MAP_LOCATIONS) {
        newState = {
            ...state,
            mapLocations: [],
            // Reset current point values as well.
            analysisLocation: undefined,
        }
    } else if (action.type === actionTypes.REMOVE_MAP_LOCATION) {
        if (action.location) {
            const idx = newState.mapLocations.findIndex(l => l.title === action.location.title)
            if (idx >= 0) {
                newState = {
                    ...state,
                    mapLocations: [...state.mapLocations.slice(0, idx), ...state.mapLocations.slice(idx + 1)],
                }
            }
        } else {
            console.warn('Undefined location passed to REMOVE_MAP_LOCATION')
        }
    } else if (action.type === actionTypes.UPDATE_MAP_LOCATION) {
        newState = { ...state }
        const idx = newState.mapLocations.findIndex(l => l.title === action.location.title)
        if (idx >= 0) {
            // merge new location information
            newState.mapLocations[idx] = { ...newState.mapLocations[idx], ...action.newState }
            newState.mapLocations[idx].valuesGrayswan = getGrayswansForLocation(newState.mapLocations[idx])
        }
    } else if (action.type === actionTypes.SET_ANALYSIS_DATA) {
        newState.analysisLocation = action.location
    } else if (action.type === actionTypes.RESET_ANALYSIS_DATA) {
        newState.mapLocations = []
    } else if (action.type === actionTypes.TOGGLE_ANALYSIS_PANEL_VISIBLE) {
        newState = {
            ...state,
            visible: !state.visible,
        }
    } else if (action.type === actionTypes.RECEIVE_FICUS_DATA) {
        // Get the grayswan data
        console.warn('Grayswan is being set from first theme in set. Make sure this is correct')
        newState = {
            ...state,
            // set grayswan if themes is a list (ie, rasters)
            grayswan: action.ficusData.themeType === 'raster' ? action.ficusData.themes[0].meta.grayswan : [],
        }
    } else if (action.type === actionTypes.SET_POINT_DATA) {
        newState = { ...state }
        let idx = newState.mapLocations.findIndex(l => l.lonlat === action.location.lonlat)
        if (idx >= 0) {
            // merge new location information
            newState.mapLocations[idx] = { ...newState.mapLocations[idx], ...action.location, title: getLocationTitle(action.location) }
        } else {
            newState.mapLocations.push({ ...action.location, title: getLocationTitle(action.location) })
            idx = newState.mapLocations.length - 1
        }
        newState.mapLocations[idx].valuesGrayswan = getGrayswansForLocation(newState.mapLocations[idx])
    }


    return newState
}
